import React, { Fragment, useEffect, useState } from "react";
import { Modal, Button, Input, Select, Radio, Space, Typography, Form, Row, Col } from "antd";
import { toast } from "react-toastify";
import { formatDateToFullString } from "../../util/date";
import { statusArray, statusOptions } from "../../data/status";
import { Prescription } from "../../types/Prescription";
import { fetchPrescriptionById, updatePrescription } from "../../api/prescription";
import PrescriptionStatusEnum from "../../types/PrescriptionStatus";
import { deepEqual, isDecimal, parseStringToDecimal } from "../../util/common";
import ProductChangeRequest from "../../types/ProductChangeRequest";
import { productChangeRequestInitialState } from "../../data/state/prescriptions";
import { otherProducts } from "../../data/otherProduct";
import { useTexts } from "../../hooks/useTexts";
import { getUserFromToken } from "../../data/storage";
import UserRole from "../../types/UserRole";

const { TextArea } = Input;
const { Title, Text } = Typography;

const PrescriptionModal = ({ open, onClose, selected, successCallback }:
    {
        open: boolean, onClose: () => void, selected: Prescription,
        successCallback: () => void
    }): JSX.Element => {
    const { t } = useTexts();
    const [status, setStatus] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [score, setScore] = useState("");
    const [productChange, setProductChange] = useState<ProductChangeRequest>(productChangeRequestInitialState);
    const [productChangeType, setProductChangeType] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const userData = getUserFromToken();
    const isManager = userData?.roles?.some(role => role.id === UserRole.MANAGER);

    const closeModal = () => {
        onClose();
        setStatus(null);
        setNote('');
        setScore("");
        setProductChange(productChangeRequestInitialState);
        setProductChangeType(null);
    };

    const update = () => {
        if (!status) return;
        const statusVal = statusArray[Number(status) - 1];
        const productChange1 = deepEqual(productChangeRequestInitialState, productChange)
            ? null
            : {
                fromOther: productChangeType === 'fromOther' ? productChange.fromOther : productChangeRequestInitialState.fromOther,
                toOther: productChangeType === 'toOther' ? productChange.toOther : productChangeRequestInitialState.toOther
            };
        
        setLoading(true);
        updatePrescription(selected.id, {
            status: statusVal as PrescriptionStatusEnum,
            note,
            score: parseStringToDecimal(score),
            productChange: productChange1
        })
            .then(data => {
                toast.success(t('prescription', 'prescriptionUpdated'), { theme: "colored" });
                successCallback();
                closeModal();
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const getStatusValue = (status: string) => {
        let index = 0;
        for (let i = 0; i < statusArray.length; i++) {
            if (statusArray[i] === status) {
                index = i;
                break;
            }
        }
        return String(index + 1);
    };

    useEffect(() => {
        const statusValue = !!selected.status ? getStatusValue(selected.status?.toString()) : null;
        setStatus(statusValue);
        setNote(selected.note || '');
    }, [selected]);

    useEffect(() => {
        if (!!selected?.id && open) {
            fetchPrescriptionById(selected.id)
                .then(response => {
                    if (!!response) {
                        if (!!response.productChange?.fromOther || !!response.productChange?.toOther) {
                            const change: any = { fromOther: {}, toOther: {} };
                            if (!!response.productChange?.fromOther) {
                                setProductChangeType('fromOther');
                                change.fromOther = {
                                    childMonth: response.productChange.fromOther.childMonth,
                                    initialMedic: response.productChange.fromOther.initialMedic,
                                    initialProduct: response.productChange.fromOther.initialProduct,
                                    productChangerId: response.productChange.fromOther.productChanger?.id,
                                };
                            }
                            if (!!response.productChange?.toOther) {
                                setProductChangeType('toOther');
                                change.toOther = {
                                    childMonth: response.productChange?.toOther?.childMonth,
                                    initialProductId: response.productChange?.toOther?.initialProduct?.id,
                                    productChanger: response.productChange?.toOther?.productChanger
                                };
                            }
                            setProductChange(change);
                        }
                    }
                })
                .catch(err => console.error(err));
        }
    }, [selected, open]);

    return (
        <Modal
            title={t('prescription', 'updatePrescription')}
            open={open}
            onCancel={closeModal}
            width={800}
            footer={[
                <Button key="back" onClick={closeModal}>
                    {t('common', 'close')}
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={update} disabled={isManager}>
                    {t('common', 'save')}
                </Button>,
            ]}
        >
            <Form layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'user')}>
                            <Input disabled value={selected.user.firstname + " " + selected.user.lastname} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'placeName')}>
                            <Input disabled value={selected.mediator?.place?.name} />
                        </Form.Item>
                    </Col>
                </Row>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'product')}>
                            <Input disabled value={selected.product.name} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'mediatorFullname')}>
                            <Input disabled value={selected.mediator?.firstname + " " + selected.mediator?.lastname} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'createdAt')}>
                            <Input disabled value={!!selected.createdAt ? formatDateToFullString(selected.createdAt) : "-"} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'updatedAt')}>
                            <Input disabled value={!!selected.updatedAt ? formatDateToFullString(selected.updatedAt) : "-"} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'contactNumber')}>
                            <Input disabled value={selected.phoneNumber} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={t('prescription', 'status')}>
                            <Select
                                value={status}
                                disabled={isManager}
                                onChange={value => setStatus(value)}
                                options={statusOptions.map(item => ({ label: t('status', item.key), value: String(item.id) }))}
                                placeholder={t('common', 'none')}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label={t('prescription', 'score')}>
                    <Input
                        value={score}
                        disabled={isManager}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (isDecimal(value) || value === '') setScore(value);
                        }}
                    />
                </Form.Item>

                <Form.Item label={t('prescription', 'notePlaceholder')}>
                    <TextArea
                        value={note}
                        disabled={isManager}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t('prescription', 'notePlaceholder')}
                        autoSize={{ minRows: 4 }}
                    />
                </Form.Item>

                <div style={{ marginTop: '24px' }}>
                    <Typography.Title level={5}>{t('productChange', 'foodChange')}</Typography.Title>
                    <Radio.Group value={productChangeType} onChange={e => setProductChangeType(e.target.value)} style={{ marginBottom: '16px' }}>
                        <Space direction="vertical">
                            <Radio value="fromOther">{t('productChange', 'toOurProduct')}</Radio>
                            <Radio value="toOther">{t('productChange', 'toOtherProduct')}</Radio>
                        </Space>
                    </Radio.Group>
                    
                    {productChangeType === 'fromOther' && (
                        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'childMonth')}>
                                        <Input
                                            value={productChange.fromOther.childMonth}
                                            disabled={isManager}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (isDecimal(value) || value === '') {
                                                    setProductChange({
                                                        ...productChange,
                                                        fromOther: { ...productChange.fromOther, childMonth: value ? Number(value) : 0 }
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'initialMedic')}>
                                        <Input
                                            value={productChange.fromOther.initialMedic}
                                            disabled={isManager}
                                            onChange={e => setProductChange({
                                                ...productChange,
                                                fromOther: { ...productChange.fromOther, initialMedic: e.target.value }
                                            })}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'otherFood')}>
                                        <Select
                                            value={productChange.fromOther.initialProductId}
                                            disabled={isManager}
                                            onChange={value => setProductChange({
                                                ...productChange,
                                                fromOther: { ...productChange.fromOther, initialProductId: value }
                                            })}
                                            options={otherProducts.map(p => ({ label: p.name, value: p.id }))}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'productChanger')}>
                                        <Input
                                            value={productChange.fromOther.productChanger}
                                            disabled={isManager}
                                            onChange={e => setProductChange({
                                                ...productChange,
                                                fromOther: { ...productChange.fromOther, productChanger: e.target.value }
                                            })}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {productChangeType === 'toOther' && (
                        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'childMonth')}>
                                        <Input
                                            value={productChange.toOther.childMonth}
                                            disabled={isManager}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (isDecimal(value) || value === '') {
                                                    setProductChange({
                                                        ...productChange,
                                                        toOther: { ...productChange.toOther, childMonth: value ? Number(value) : 0 }
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'otherFood')}>
                                        <Select
                                            value={productChange.toOther.otherProductId}
                                            disabled={isManager}
                                            onChange={value => setProductChange({
                                                ...productChange,
                                                toOther: { ...productChange.toOther, otherProductId: value }
                                            })}
                                            options={otherProducts.map(p => ({ label: p.name, value: p.id }))}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label={t('productChange', 'productChanger')}>
                                        <Input
                                            value={productChange.toOther.productChanger}
                                            disabled={isManager}
                                            onChange={e => setProductChange({
                                                ...productChange,
                                                toOther: { ...productChange.toOther, productChanger: e.target.value }
                                            })}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            </Form>
        </Modal>
    );
};

export default PrescriptionModal;